/**
 * UserBotCustomizer — small dialog the AI chat header opens so each
 * employee can pick their own bot name + picture without affecting
 * coworkers. Backed by /ai/user/bot-config (GET/PUT/DELETE).
 *
 * UX choices:
 *   - The Name + Picture inputs show the *user override only*, NOT
 *     the company fallback. That keeps the dialog honest about what
 *     is yours vs what's inherited.
 *   - Picture accepts a URL paste OR an upload via /upload (the same
 *     endpoint the PolicyEditor uses), so power users and casual
 *     users both have a path.
 *   - "Reset" calls DELETE so the user goes back to the company
 *     default. The caption tells them what that default is, so they
 *     know what they're falling back to.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Avatar, Typography, IconButton, Tooltip,
  Box,
} from '@mui/material';
import { GlassSpinner } from '../common/GlassLoader';
import { Close, CloudUpload, RestartAlt, AutoAwesome } from '@mui/icons-material';
import toast from 'react-hot-toast';
import apiClient from '../../services/api/config';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { useUserBot } from '../../hooks/useUserBot';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function UserBotCustomizer({ open, onClose }: Props) {
  const bot = useUserBot();
  const [name, setName] = useState('');
  const [picture, setPicture] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Reset the form whenever the dialog opens so a previous edit
  // doesn't bleed into the next session.
  useEffect(() => {
    if (!open) return;
    setName(bot.userOverride.botName || '');
    setPicture(bot.userOverride.botPicture || '');
  }, [open, bot.userOverride.botName, bot.userOverride.botPicture]);

  const previewName = (name || bot.companyDefault.botName || 'Aira').trim();
  const previewPicture = picture || bot.companyDefault.botPicture || '';

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/ai/user/bot-config', {
        botName: name.trim() || null,
        botPicture: picture.trim() || null,
      });
      await bot.refresh();
      toast.success('Saved');
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to save';
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `${msg}: ${detail}` : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to your company default?')) return;
    setSaving(true);
    try {
      await apiClient.delete('/ai/user/bot-config');
      await bot.refresh();
      toast.success(`Reset to ${bot.companyDefault.botName}`);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url || res.data?.url;
      if (url) {
        setPicture(url);
        toast.success('Image uploaded');
      } else {
        toast.error('Upload returned no URL');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AutoAwesome sx={{ color: '#6366F1' }} />
          <Typography variant="h6" fontWeight={700}>
            Customize your AI bot
          </Typography>
        </Stack>
        <IconButton onClick={onClose} disabled={saving}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Live preview at the top — what the chat header will show */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 2, mb: 2, borderRadius: 3,
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            color: 'white',
          }}
        >
          <Avatar
            src={previewPicture ? resolveAssetUrl(previewPicture) : undefined}
            sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.3)' }}
          >
            {!previewPicture && (previewName.charAt(0).toUpperCase() || 'A')}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>
              Ask {previewName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {bot.source === 'user' ? 'Your custom bot' : `${bot.companyDefault.botName} (company default)`}
            </Typography>
          </Box>
        </Box>

        <Stack spacing={2}>
          <TextField
            label="Bot name"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={bot.companyDefault.botName || 'Aira'}
            helperText={`Leave blank to use the company default (${bot.companyDefault.botName || 'Aira'}).`}
            inputProps={{ maxLength: 40 }}
          />

          <Box>
            <TextField
              label="Picture URL"
              size="small"
              fullWidth
              value={picture}
              onChange={(e) => setPicture(e.target.value)}
              placeholder="https:// or /uploads/..."
              helperText="Paste a URL or upload below."
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                component="label"
                startIcon={uploading ? <GlassSpinner size={14} /> : <CloudUpload fontSize="small" />}
                disabled={uploading}
                sx={{ borderRadius: '12px' }}
              >
                Upload image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                  }}
                />
              </Button>
              {picture && (
                <Tooltip title="Clear the picture (revert to company default image)">
                  <Button size="small" variant="text" onClick={() => setPicture('')} disabled={uploading}>
                    Clear picture
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Tooltip title={bot.source === 'user' ? `Reset to ${bot.companyDefault.botName}` : 'Already on the company default'}>
          <span>
            <Button
              startIcon={<RestartAlt />}
              onClick={handleReset}
              disabled={saving || bot.source !== 'user'}
              size="small"
            >
              Reset
            </Button>
          </span>
        </Tooltip>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
            {saving ? <GlassSpinner size={18} /> : 'Save'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
